import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Receipt, Calculator, PenTool, ShieldCheck, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyPurchases() {
  const { authHeaders } = useAuth();
  const [data, setData] = useState({ entitlements: [], unlimited_access: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/billing/entitlements`, { headers: authHeaders })
      .then(r => { if (mounted) setData(r.data || { entitlements: [], unlimited_access: false }); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [authHeaders]);

  if (loading) {
    return (
      <Card data-testid="my-purchases" className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl mt-6">
        <CardContent className="p-6 text-slate-400 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading purchases…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="my-purchases" className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl mt-6 overflow-hidden">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-fern" />
            <h2 className="font-heading text-xl font-semibold text-fern-dark">My Purchases</h2>
          </div>
          <Link to="/pricing">
            <Button data-testid="purchases-pricing-cta" variant="outline" size="sm" className="rounded-full border-fern/30 text-fern hover:bg-fern/5">
              Buy more access
            </Button>
          </Link>
        </div>

        {data.unlimited_access && (
          <div className="rounded-2xl border border-fern/20 bg-fern/5 p-4 mb-4 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-fern flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700">
              <strong>Admin / unlimited access</strong> is enabled on this account — every lesson is unlocked for every child.
            </p>
          </div>
        )}

        {data.entitlements.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center">
            <p className="text-sm text-slate-500 mb-3">No purchases yet.</p>
            <Link to="/pricing">
              <Button data-testid="purchases-empty-cta" className="bg-fern hover:bg-fern-light text-white rounded-full">
                See Pricing
              </Button>
            </Link>
          </div>
        ) : (
          <ul className="space-y-3">
            {data.entitlements.map((e) => {
              const subjLabel = e.subject === 'maths' ? 'Pāngarau / Maths' : 'Reo Pākehā / English';
              const Icon = e.subject === 'maths' ? Calculator : PenTool;
              const accent = e.subject === 'maths' ? 'moana' : 'fern';
              const purchasedDate = e.purchased_at ? new Date(e.purchased_at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
              return (
                <li key={e.id} data-testid={`purchase-${e.order_id}`} className="rounded-2xl border border-slate-100 bg-slate-50/40 p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${accent === 'moana' ? 'bg-moana-50 text-moana' : 'bg-fern-50 text-fern'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="font-medium text-fern-dark">
                          {subjLabel} <span className="text-slate-400">·</span> <span className="text-moana">{e.child_name}</span>
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Order #{e.order_identifier || e.order_id} · purchased {purchasedDate} · {e.plan === 'weekly' ? `Week ${e.week || '?'} of Term ${e.term || '?'}` : `Term ${e.unlocked_through_term || 4} of 4 unlocked`}
                        </p>
                      </div>
                    </div>
                    {e.status === 'refunded' ? (
                      <Badge className="bg-slate-200 text-slate-600 border-0 rounded-full">Refunded</Badge>
                    ) : (
                      <Badge className="bg-fern text-white border-0 rounded-full">Active</Badge>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
